import {
  type BillingCheckoutInput,
  type BillingInterval,
  type BillingPlan,
  PLAN_LIMITS,
  type PlanLimits,
  SELF_SERVE_PLANS,
  type SelfServePlan,
  type TaxMode,
} from '@af/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { IconCheck } from '@/components/ui/icons';
import { formatEuro, yearlyDeal } from '@/lib/plan-pricing';
import { cn } from '@/lib/utils';

export const PLAN_NAMES: Record<string, string> = {
  starter: 'Starter',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
};

/** Capability lines per tier (website pricing page); the numbers come from `PLAN_LIMITS`. */
const FEATURES: Record<SelfServePlan, string[]> = {
  starter: ['tasks', 'approvals', 'memory', 'subscriptions', 'notifications'],
  team: [
    'everythingStarter',
    'hireAgents',
    'gitWorkspaces',
    'browserFlows',
    'chatChannels',
    'mcp',
    'analysis',
  ],
  business: ['everythingTeam', 'audit', 'priority', 'onboarding'],
};
const POPULAR: SelfServePlan = 'team';

export function limitLines(
  l: PlanLimits,
  locale: string,
  t: (key: string, o?: Record<string, unknown>) => string,
): string[] {
  const n = (v: number | null) => (v === null ? t('billing.unlimited') : v.toLocaleString(locale));
  return [
    t('billing.planOrg', { companies: n(l.companies), agents: n(l.agents), seats: n(l.seats) }),
    t('billing.planRuns', { runs: n(l.runsPerMonth) }),
    t('billing.planSandbox', { sandbox: n(l.sandboxHoursPerMonth) }),
  ];
}

export function PlanPicker({
  plans,
  taxMode,
  suggested,
  busy,
  locale,
  onChoose,
}: {
  plans: BillingPlan[];
  taxMode: TaxMode;
  suggested: BillingCheckoutInput | null;
  busy: boolean;
  locale: string;
  onChoose: (choice: BillingCheckoutInput) => void;
}) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<BillingInterval>(suggested?.interval ?? 'month');
  const price = (plan: SelfServePlan, i: BillingInterval) =>
    plans.find((p) => p.plan === plan && p.interval === i)?.amount;
  const offered = SELF_SERVE_PLANS.filter((p) => price(p, 'month') !== undefined);
  // The toggle advertises the best real deal across the tiers, never a hardcoded number.
  const deals = offered.flatMap((p) => {
    const m = price(p, 'month');
    const y = price(p, 'year');
    return m !== undefined && y !== undefined ? [yearlyDeal(m, y)] : [];
  });
  const best = deals.reduce<ReturnType<typeof yearlyDeal> | null>(
    (a, d) => (!a || d.percent > a.percent ? d : a),
    null,
  );
  const yearLabel =
    best && best.percent > 0
      ? best.monthsFree
        ? t('billing.yearDealMonths', { months: best.monthsFree })
        : t('billing.yearDealPercent', { percent: best.percent })
      : null;
  const highlighted = suggested?.plan ?? POPULAR;

  return (
    <section className="flex flex-col gap-6" aria-label={t('billing.choosePlan')}>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <fieldset className="m-0 inline-flex min-w-0 items-center gap-1 rounded-lg border border-border bg-panel p-1">
          <legend className="sr-only">{t('billing.interval')}</legend>
          {(['month', 'year'] as const).map((i) => (
            <button
              key={i}
              type="button"
              aria-pressed={interval === i}
              onClick={() => setInterval(i)}
              className={cn(
                'h-8 rounded-md px-4 text-sm text-muted transition-colors hover:text-fg',
                'aria-pressed:bg-cta aria-pressed:font-medium aria-pressed:text-cta-fg',
              )}
            >
              {t(i === 'month' ? 'billing.intervalMonth' : 'billing.intervalYear')}
              {i === 'year' && yearLabel && <span className="ml-1.5 opacity-80">· {yearLabel}</span>}
            </button>
          ))}
        </fieldset>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {offered.map((plan) => {
          const monthly = price(plan, 'month');
          const amount = price(plan, interval);
          if (monthly === undefined || amount === undefined) return null;
          const yearly = interval === 'year';
          const deal = yearly ? yearlyDeal(monthly, amount) : null;
          const isHighlighted = highlighted === plan;
          const isSuggested = suggested?.plan === plan;
          const lines = limitLines(PLAN_LIMITS[plan], locale, t);
          return (
            <div
              key={plan}
              className={cn(
                'relative flex flex-col rounded-xl border bg-bg p-6',
                isHighlighted ? 'border-accent shadow-soft' : 'border-border',
              )}
            >
              {(isSuggested || plan === POPULAR) && (
                <span className="absolute -top-2.5 left-5 rounded-md bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-fg">
                  {t(isSuggested ? 'billing.suggested' : 'billing.popular')}
                </span>
              )}
              <h3 className="text-base font-medium">{PLAN_NAMES[plan]}</h3>
              <p className="mt-1 min-h-10 text-sm text-muted">{t(`billing.blurb.${plan}`)}</p>

              <div className="mt-5 flex flex-wrap items-baseline gap-x-2">
                <span className="text-4xl font-semibold tracking-tight">
                  {formatEuro(deal ? deal.perMonthCents : amount, locale)}
                </span>
                <span className="text-sm text-muted">{t('billing.perMonthShort')}</span>
                {deal && (
                  <span className="text-sm text-faint line-through">{formatEuro(monthly, locale)}</span>
                )}
              </div>
              <p className="mt-1 min-h-9 text-xs text-muted">
                {deal
                  ? t('billing.billedYearly', {
                      total: formatEuro(amount, locale),
                      saved: formatEuro(deal.savedCents, locale),
                    })
                  : t('billing.billedMonthly')}
                {' · '}
                {t(taxMode === 'kor' ? 'billing.noVat' : 'billing.exVat')}
              </p>

              <Button
                className="mt-4 h-10 w-full justify-center"
                variant={isHighlighted ? 'primary' : 'default'}
                disabled={busy}
                onClick={() => onChoose({ plan, interval })}
              >
                {t('billing.choose', { plan: PLAN_NAMES[plan] })}
              </Button>

              <ul className="mt-6 flex flex-col gap-2.5 text-sm">
                {lines.map((l) => (
                  <li key={l} className="flex gap-2 font-medium">
                    <IconCheck size={15} className="mt-0.5 shrink-0 text-success" />
                    {l}
                  </li>
                ))}
                {FEATURES[plan].map((f) =>
                  f.startsWith('everything') ? (
                    <li key={f} className="pt-1 text-xs font-medium text-muted">
                      {t(`billing.features.${f}`)}
                    </li>
                  ) : (
                    <li key={f} className="flex gap-2 text-fg-2">
                      <IconCheck size={15} className="mt-0.5 shrink-0 text-success" />
                      {t(`billing.features.${f}`)}
                    </li>
                  ),
                )}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-center text-sm text-muted">{t('billing.terms')}</p>
    </section>
  );
}
